import fs from 'node:fs';
import { getBridgeRoot } from '../cwd/index.js';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { LINE_CHUNK_SIZE, type ReadFileResult } from './types.js';
import { resolveFileForReadAsync, resolveFilePathAsync } from './resolve-file-path.js';
import { readFileRangeAsync } from './read-file-range-async.js';
import { readSmallFileRangeAsync, SMALL_FILE_RANGE_MAX_BYTES } from './read-small-file-range-async.js';
import { readFileFullAsync } from './read-file-full-async.js';
import { readFileBinaryFullAsync } from './read-file-binary-full-async.js';
import type { FileReadEncoding, ReadFileOptions } from './read-file-options.js';
import {
  isInteractiveMainThreadRead,
  readFileInteractiveOnMainThread,
} from './read-file-interactive-main-thread.js';

/**
 * Read file content asynchronously. Yields before I/O and during ranged reads so the bridge stays responsive.
 * - Without range: reads full file via async fs APIs (chunked with yields).
 * - With range: streams through file extracting only the requested lines (memory-efficient).
 * - encoding `base64`: full binary read (no range); for images and downloads.
 */
export async function readFileAsync(
  relativePath: string,
  startLine?: number,
  endLine?: number,
  lineOffset?: number,
  lineChunkSize: number = LINE_CHUNK_SIZE,
  sessionParentPath: string = getBridgeRoot(),
  encoding: FileReadEncoding = 'utf8',
  options?: ReadFileOptions,
): Promise<ReadFileResult | { error: string }> {
  if (isInteractiveMainThreadRead(options) && encoding === 'utf8') {
    const hasRange = typeof startLine === 'number' && typeof endLine === 'number';
    if (hasRange) {
      return readFileInteractiveOnMainThread(
        relativePath,
        startLine,
        endLine,
        lineOffset,
        lineChunkSize,
        sessionParentPath,
      );
    }
  }

  if (!options?.skipInitialYield) {
    await yieldToEventLoop();
  }

  try {
    let resolvedPath: string;
    let fileSize: number | undefined;
    if (options?.useResolvedSize) {
      const resolved = await resolveFileForReadAsync(relativePath, sessionParentPath);
      if ('error' in resolved) return resolved;
      resolvedPath = resolved.path;
      fileSize = resolved.size;
    } else {
      const result = await resolveFilePathAsync(relativePath, sessionParentPath);
      if (typeof result === 'object') return result;
      resolvedPath = result;
    }

    const hasRange = typeof startLine === 'number' && typeof endLine === 'number';
    if (encoding === 'base64') {
      if (hasRange) return { error: 'base64 encoding requires a full file read (no line range)' };
      const read = await readFileBinaryFullAsync(resolvedPath);
      return { ...read, resolvedPath };
    }
    if (hasRange) {
      if (fileSize == null) {
        const stat = await fs.promises.stat(resolvedPath);
        fileSize = stat.size;
      }

      const read =
        fileSize <= SMALL_FILE_RANGE_MAX_BYTES
          ? await readSmallFileRangeAsync(
              resolvedPath,
              fileSize,
              startLine,
              endLine,
              lineOffset,
              lineChunkSize,
            )
          : await readFileRangeAsync(
              resolvedPath,
              startLine,
              endLine,
              lineOffset,
              lineChunkSize,
              fileSize,
            );
      return read;
    }
    const read = await readFileFullAsync(resolvedPath, fileSize);
    return { ...read, resolvedPath };
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}
